import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle, Loader2, Car } from 'lucide-react';
import axios from 'axios';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const checkPaymentStatus = useCallback(async () => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/status/${sessionId}`);
      setPaymentStatus(response.data);
      
      // If payment is still processing, retry a few times
      if (response.data.status === 'checking' || response.data.status === 'session_not_found') {
        if (retryCount < 5) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000);
        }
      }
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error checking payment:', err);
      if (retryCount < 3) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 2000);
      } else {
        setError('Unable to verify payment status. Your payment may still be processing.');
        setLoading(false);
      }
    }
  }, [BACKEND_URL, sessionId, retryCount]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full p-8 text-center">
        <img src={logoUrl} alt="Logo" className="h-16 w-16 mx-auto mb-6 object-contain" />
        
        {loading ? (
          <div className="space-y-4">
            <Loader2 className="h-16 w-16 text-amber-500 animate-spin mx-auto" />
            <h2 className="text-2xl font-bold">Confirming Your Payment...</h2>
            <p className="text-gray-600">Please wait while we process your booking</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="text-green-600" size={48} />
            </div>
            
            <div>
              <h2 className="text-3xl font-bold mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-4">Your ride has been booked</p>
            </div>

            {paymentStatus?.driver_assigned ? (
              <Card className="p-4 bg-amber-50 border-amber-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Car className="text-amber-600" size={20} />
                  <span className="font-semibold">Driver Assigned</span>
                </div>
                <p className="text-sm text-gray-700">
                  Driver: <strong>{paymentStatus.driver?.driver_name}</strong>
                </p>
                <p className="text-sm text-gray-700">
                  ETA: <strong>{paymentStatus.driver?.eta} minutes</strong>
                </p>
              </Card>
            ) : (
              <Card className="p-4 bg-blue-50 border-blue-200">
                <p className="text-sm text-blue-800">
                  We're searching for the best driver in your area...
                </p>
              </Card>
            )}

            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Ride ID: <strong>{paymentStatus?.ride_id}</strong>
              </p>
              <p className="text-sm text-gray-600">
                A confirmation has been sent to your phone
              </p>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={() => navigate('/user')}
                variant="outline"
                className="flex-1 border-2"
              >
                Book Another Ride
              </Button>
              <Button 
                onClick={() => navigate(`/user/track/${paymentStatus?.ride_id}`)}
                className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700"
              >
                Track Ride
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PaymentSuccess;